<template>
  <UIImageCropper
    :model-value="image"
    :loading
    :title
    :display-aspect-ratio="8 / 1"
    :readonly="isReadonly"
    @update:model-value="handleUpdate"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch, inject } from 'vue'
import { AssetClass } from '..'

import { DocumentSource, ReadonlyMode } from '@types'

import { ASSET_FIELD, READONLY_MODE } from '@/const'

import { handleCatchedError } from '@/helpers/common'

import { useDocumentsStore } from '@/store/documents'
import { useDocumentsBunchStore } from '@/store/documents/bunch'

import { UIImageCropper } from '@ui/controllers'

type Props = {
  instance: AssetClass
}

const props = defineProps<Props>()

const documentsStore = useDocumentsStore()
const documentsBunchStore = useDocumentsBunchStore()

const isReadonly = inject<ReadonlyMode>(READONLY_MODE)

const image = ref<string>()
const loading = ref(false)

const title = computed(() => {
  return `${props.instance.field<string>(ASSET_FIELD.NAME).value} Image`
})

const handleUpdate = async (data?: File | string) => {
  loading.value = true
  try {
    if (props.instance.thumbnailId) {
      const document = documentsBunchStore.getElementById(props.instance.thumbnailId)
      if (document) {
        await documentsBunchStore.unshiftElement(document)
      }
      await documentsStore.destroy(props.instance.thumbnailId)
      props.instance.docs = props.instance.docs.filter(doc => doc !== props.instance.thumbnailId)
      props.instance.thumbnailId = undefined
    }

    if (!data) return

    const filename = (data as File)?.name || ''

    const file = new FormData()
    file.append('file', data as File)
    file.append('filename', filename)

    const document = documentsBunchStore.createElement()
    document.set({
      name: filename,
      source: DocumentSource.ALLPOSIT_THUMBNAIL,
      asset_id: props.instance.id,
      filename,
      file: data as unknown as FormData,
    })
    await document.store()
  } catch (e) {
    handleCatchedError(e as string)
  } finally {
    loading.value = false
  }
}

watch(() => props.instance.thumbnailId, async value => {
  if (!value) {
    image.value = undefined
    return
  }
  if (props.instance.thumbnailUrl) {
    image.value = props.instance.thumbnailUrl
    return
  }
  try {
    loading.value = true
    const document = await documentsStore.fetchSingle(value)
    const thumbnail = document.url
    if (!thumbnail) return
    props.instance.thumbnailUrl = thumbnail
    image.value = thumbnail
  } catch (e) {
    handleCatchedError(e as string)
  } finally {
    loading.value = false
  }
}, { immediate: true })
</script>
